import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import { getError } from '../utils';
import { calculateKeycapSizesForMultipleLayouts } from '../utils/keycapSizes';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import Table from 'react-bootstrap/Table';
import { OverlayTrigger, Popover, Form, Nav } from 'react-bootstrap';
import Pagination from '../components/general/Pagination';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        layouts: action.payload.layouts,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'FETCH_ALL_REQUEST':
      return { ...state, loadingAll: true, errorAll: '' };
    case 'FETCH_ALL_SUCCESS':
      return {
        ...state,
        loadingAll: false,
      };
    case 'FETCH_ALL_FAIL':
      return { ...state, loadingAll: false, errorAll: action.payload }; // its own error: the statistics tab shows it, the list stays usable

    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };

    default:
      return state;
  }
};

export default function LayoutListScreen() {
  const [
    { loading, error, loadingAll, errorAll, loadingCreate, layouts, pages },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    error: '',
    loadingAll: true,
    errorAll: '',
    layouts: [],
  });

  const navigate = useNavigate();

  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present

  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input

  const [keycapFrequencies, setKeycapFrequencies] = useState([]);
  const [keycapFilter, setKeycapFilter] = useState('Uniform');
  const [filter, setFilter] = useState('layouts');

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_ALL_REQUEST' });
      try {
        const { data } = await api.get(`/api/layouts/all`);

        setKeycapFrequencies(calculateKeycapSizesForMultipleLayouts(data));

        dispatch({ type: 'FETCH_ALL_SUCCESS' });
      } catch (err) {
        dispatch({ type: 'FETCH_ALL_FAIL', payload: getError(err) });
      }
    };

    fetchData();
  }, []); // this useEffect hook fetches and calculates the data needed for the layout statistics table

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await api.get(
          `/api/layouts?page=${page}&search=${encodeURIComponent(searchQuery)}`
        );

        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };

    fetchData();
  }, [page, searchQuery]);

  const createHandler = async () => {
    if (window.confirm('Willst du ein neues layout erstellen?')) {
      // if the user confirms that he wants to create a new product, the following code will run
      try {
        dispatch({ type: 'CREATE_REQUEST' });
        const { data } = await api.post('/api/layouts', {}); // posts a new product to the '/api/layouts' backend api
        toast.success('Layout erstellt');
        dispatch({ type: 'CREATE_SUCCESS' });
        navigate(`/admin/layout/${data.layout._id}`); // navigates the user to the newly created product
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: 'CREATE_FAIL',
        });
      }
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.replace(/\s+/g, ''); // Remove spaces
    navigate(
      sanitizedInput
        ? `/admin/layouts?page=1&search=${encodeURIComponent(sanitizedInput)}`
        : `/admin/layouts?page=1`
    ); // Navigates to the LayoutListScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  return (
    <Container>
      <div>
        <title>Layouts</title>

        <h1 className="screen-heading">Layouts</h1>

        <Nav
          justify
          variant="tabs"
          activeKey={filter}
          onSelect={(newFilterKey) => {
            setFilter(newFilterKey);
          }}
          className="customizer-nav"
        >
          <Nav.Item>
            <Nav.Link eventKey="layouts">Layouts</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="statistics">Statistics</Nav.Link>
          </Nav.Item>
        </Nav>

        {loading || loadingCreate || (filter === 'statistics' && loadingAll) ? ( // the create overlay as on the other list screens (its flag was never read here, so a second click created a second layout), and the statistics tab waits for the /all result it maps over (opened before it arrived, it threw); both fixed Oct 2026
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            {filter === 'statistics' && errorAll ? (
              <MessageBox className="danger-alert">{errorAll}</MessageBox>
            ) : filter === 'statistics' ? (
              <div className="mt-3">
                <Row>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Keycap</th>
                        <th>
                          <div className="d-flex">
                            Max Frequency
                            <OverlayTrigger
                              trigger={['click', 'hover']}
                              rootClose // close the popover when the user clicks outside of it
                              placement="bottom"
                              overlay={
                                <Popover>
                                  <Popover.Body>
                                    The highest frequency of this keycap size in
                                    a single layout.
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <Button className="info-popover">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="#707070"
                                  className="bi bi-info-circle"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                </svg>
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </th>
                        <th>
                          <div className="d-flex">
                            Layout Count
                            <OverlayTrigger
                              trigger={['click', 'hover']}
                              rootClose // close the popover when the user clicks outside of it
                              placement="bottom"
                              overlay={
                                <Popover>
                                  <Popover.Body>
                                    In how many different layouts this keycap
                                    size is used.
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <Button className="info-popover">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="#707070"
                                  className="bi bi-info-circle"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                </svg>
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </th>
                        <th>
                          <div className="d-flex">
                            Total Frequency
                            <OverlayTrigger
                              trigger={['click', 'hover']}
                              rootClose // close the popover when the user clicks outside of it
                              placement="bottom"
                              overlay={
                                <Popover>
                                  <Popover.Body>
                                    The frequency for all layouts together
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <Button className="info-popover">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="#707070"
                                  className="bi bi-info-circle"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                  <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                </svg>
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {keycapFilter === 'Uniform'
                        ? keycapFrequencies.keycapSizes.map(
                            ([
                              size,
                              total,
                              layoutCount,
                              maxFrequency,
                              layouts,
                              maxFrequencyLayouts,
                            ]) => (
                              <tr key={size}>
                                <td>{size}</td>
                                <td>
                                  <div className="d-flex">
                                    <OverlayTrigger
                                      trigger={['click', 'hover']}
                                      rootClose // close the popover when the user clicks outside of it
                                      placement="bottom"
                                      overlay={
                                        <Popover>
                                          <Popover.Body>
                                            <ul>
                                              {maxFrequencyLayouts.map(
                                                (layout, index) => (
                                                  <li key={index}>{layout}</li>
                                                )
                                              )}
                                            </ul>
                                          </Popover.Body>
                                        </Popover>
                                      }
                                    >
                                      <Button className="info-popover">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="#707070"
                                          className="bi bi-info-circle"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                          <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                        </svg>
                                      </Button>
                                    </OverlayTrigger>
                                    {maxFrequency}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex">
                                    <OverlayTrigger
                                      trigger={['click', 'hover']}
                                      rootClose // close the popover when the user clicks outside of it
                                      placement="bottom"
                                      overlay={
                                        <Popover>
                                          <Popover.Body>
                                            <ul>
                                              {layouts.map((layout, index) => (
                                                <li key={index}>{layout}</li>
                                              ))}
                                            </ul>
                                          </Popover.Body>
                                        </Popover>
                                      }
                                    >
                                      <Button className="info-popover">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="#707070"
                                          className="bi bi-info-circle"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                          <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                        </svg>
                                      </Button>
                                    </OverlayTrigger>
                                    {layoutCount}
                                  </div>
                                </td>
                                <td>{total}</td>
                              </tr>
                            )
                          )
                        : keycapFrequencies.keycapSizesWithRow.map(
                            ([
                              size,
                              total,
                              layoutCount,
                              maxFrequency,
                              layouts,
                              maxFrequencyLayouts,
                            ]) => (
                              <tr key={size}>
                                <td>{size}</td>
                                <td>
                                  <div className="d-flex">
                                    <OverlayTrigger
                                      trigger={['click', 'hover']}
                                      rootClose // close the popover when the user clicks outside of it
                                      placement="bottom"
                                      overlay={
                                        <Popover>
                                          <Popover.Body>
                                            <ul>
                                              {maxFrequencyLayouts.map(
                                                (layout, index) => (
                                                  <li key={index}>{layout}</li>
                                                )
                                              )}
                                            </ul>
                                          </Popover.Body>
                                        </Popover>
                                      }
                                    >
                                      <Button className="info-popover">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="#707070"
                                          className="bi bi-info-circle"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                          <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                        </svg>
                                      </Button>
                                    </OverlayTrigger>
                                    {maxFrequency}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex">
                                    <OverlayTrigger
                                      trigger={['click', 'hover']}
                                      rootClose // close the popover when the user clicks outside of it
                                      placement="bottom"
                                      overlay={
                                        <Popover>
                                          <Popover.Body>
                                            <ul>
                                              {layouts.map((layout, index) => (
                                                <li key={index}>{layout}</li>
                                              ))}
                                            </ul>
                                          </Popover.Body>
                                        </Popover>
                                      }
                                    >
                                      <Button className="info-popover">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          fill="#707070"
                                          className="bi bi-info-circle"
                                          viewBox="0 0 16 16"
                                        >
                                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                          <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                        </svg>
                                      </Button>
                                    </OverlayTrigger>
                                    {layoutCount}
                                  </div>
                                </td>
                                <td>{total}</td>
                              </tr>
                            )
                          )}
                    </tbody>
                  </Table>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <div className="d-flex justify-content-end">
                      <Button
                        className="mx-2"
                        onClick={() => setKeycapFilter('Uniform')}
                        variant={keycapFilter === 'Uniform' ? 'dark' : 'light'}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          fill="currentColor"
                          className="bi bi-list"
                          viewBox="0 0 16 16"
                        >
                          <path
                            fillRule="evenodd"
                            d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
                          />
                        </svg>
                      </Button>
                      <Button
                        className="mx-2"
                        onClick={() => setKeycapFilter('Row')}
                        variant={keycapFilter === 'Row' ? 'dark' : 'light'}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="22"
                          height="22"
                          fill="currentColor"
                          className="bi bi-list-ol"
                          viewBox="0 0 16 16"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5"
                          />
                          <path d="M1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635z" />
                        </svg>
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <div>
                {/* Search Form */}
                <Form onSubmit={submitHandler} className="d-flex my-3">
                  <Form.Control
                    type="text"
                    placeholder="Search by layoutName, alternativeNames, keyboardModels or _id"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    aria-label="Search by layoutName, alternativeNames, keyboardModels or _id"
                  />
                  <Button type="submit" variant="secondary" className="mx-3">
                    Search
                  </Button>
                </Form>

                <Row className="my-3">
                  <Row>
                    <Col>
                      <div className="d-flex justify-content-center my-4">
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={createHandler}
                        >
                          Layout erstellen
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Row className="d-xl-none">
                    {layouts ? (
                      layouts.map((layout) => (
                        <Col key={layout._id} xs={12} sm={12} md={12} lg={6}>
                          <Card className="products-card">
                            <Card.Body className="text-center">
                              <Card.Title className="code mb-4">
                                {layout.layoutName}
                              </Card.Title>
                              <div className="divider"></div>
                              <div className="code-info">
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Tags:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {layout.tags && layout.tags.length > 0
                                      ? layout.tags.join(', ')
                                      : 'None'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Finished:
                                  </strong>{' '}
                                  <span className="no-wrap">
                                    {layout.isFinished ? 'Yes' : 'No'}
                                  </span>
                                </Card.Text>
                                <Card.Text>
                                  <strong
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    Aktionen:
                                  </strong>
                                  <span className="no-wrap w-100 mt-1">
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() =>
                                        navigate(`/admin/layout/${layout._id}`)
                                      }
                                    >
                                      Bearbeiten
                                    </Button>
                                    &nbsp;
                                    <Button
                                      variant="secondary"
                                      type="button"
                                      onClick={() => {
                                        toast.info(
                                          'This button is disabled, as Layout documents can be referenced inside Design documents and therefor the Designs rely on the Layout documents. Instead you have the opportunity to set the isActive state of the Product of this Layout to false, which removes this Layout from the Customizer and all existing Designs that rely on this Layout are no longer available for purchase.'
                                        );
                                      }}
                                    >
                                      Löschen
                                    </Button>
                                  </span>
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div className="w-100">
                        <MessageBox>no layouts found</MessageBox>
                      </div>
                    )}
                  </Row>
                  <div className="d-none d-xl-block">
                    <table
                      className="table"
                      style={{ tableLayout: 'fixed', width: '100%' }}
                    >
                      <thead>
                        <tr>
                          <th style={{ width: '50%' }}>NAME</th>
                          <th>Size</th>
                          <th>TAGS</th>
                          <th>FINISHED</th>
                          <th>AKTIONEN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {layouts ? (
                          layouts.map((layout) => (
                            <tr key={layout._id}>
                              <td
                                style={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {layout.layoutName}
                              </td>
                              <td
                                style={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {layout.size}
                              </td>
                              <td>
                                {layout.tags && layout.tags.length > 0
                                  ? layout.tags.join(', ')
                                  : 'None'}
                              </td>
                              <td>{layout.isFinished ? 'Yes' : 'No'}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() =>
                                    navigate(`/admin/layout/${layout._id}`)
                                  }
                                >
                                  Bearbeiten
                                </Button>
                                &nbsp;
                                <Button
                                  variant="secondary"
                                  type="button"
                                  onClick={() => {
                                    toast.info(
                                      'This button is disabled, as Layout documents can be referenced inside Design documents and therefor the Designs rely on the Layout documents. Instead you have the opportunity to set the isActive state of the Product of this Layout to false, which removes this Layout from the Customizer and all existing Designs that rely on this Layout are no longer available for purchase.'
                                    );
                                  }}
                                >
                                  Löschen
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <div className="w-100">
                            <MessageBox>no layouts found</MessageBox>
                          </div>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Row>

                <Pagination
                  pages={pages}
                  page={Number(page)}
                  baseUrl="/admin/layouts"
                  search={searchQuery}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Container>
  );
}
